import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CategoryUpdate = () => {
    const { srno } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    const baseUrl = process.env.REACT_APP_API_URL;
    const url = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;

    const [formData, setFormData] = useState({
        categoryid: '',
        categoryname: '',
        categorydisplayname: '',
        categorydetails: '',
        isactive: 1
    });

    // Fetch single category
    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const headers = {
                    'x-access-token': localStorage.getItem('jwttoken')
                };

                const response = await axios.get(
                    `${url}postcategory/getOne/${srno}`, 
                    { headers });

                if (response.data) {
                    setFormData({
                        categoryid: response.data.categoryid || '',
                        categoryname: response.data.categoryname || '',
                        categorydisplayname: response.data.categorydisplayname || '',
                        categorydetails: response.data.categorydetails || '',
                        isactive: response.data.isactive
                    });
                }
            } catch (err) {
                console.error("Fetch Error:", err);
                toast.error("Failed to load category data.",{
                    position: "top-right",
                    autoClose: 1000,
                    theme: "colored",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchCategory();
    }, [srno, url]);

    const handleChange = (e) => {
        const { id, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));;
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        // Validation
        if (!formData.categoryid || !formData.categoryname.trim() || !formData.categorydisplayname.trim()) {
            toast.error("Please fill in all required fields.");
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmUpdate = async () => {
        setShowConfirmModal(false);
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('jwttoken');
            const headers = { 'x-access-token': token };

            const updateData = {
                categoryid: formData.categoryid, 
                categoryname: formData.categoryname.trim(),
                categorydisplayname: formData.categorydisplayname.trim(),
                categorydetails: formData.categorydetails.trim(),
                isactive: formData.isactive 
            };

            await axios.put(
                `${url}postcategory/updates/${srno}`, 
                updateData, 
                { headers }
            );

            toast.success('Category Updated Successfully', {
                position: "top-right",
                autoClose: 1000,
                theme: "colored",
            });

            setTimeout(() => {
                navigate("/categorylist");
            }, 1000);

        } catch (err) {
            const msg = err.response?.data?.message || 'Update failed.';
            toast.error(msg,{
                position: "top-right",
                autoClose: 1000,
                theme: "colored",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className="text-center mt-5">
            <Spinner animation="border" variant="primary" />
            <p>Loading Category Details...</p>
        </div>
    );

    return (
        <div className="container mt-4">
            <div className="form-card">
                <div className="form-card-header">Update Category Details</div>
                <div className="form-card-body">
                    <Form onSubmit={handleUpdateSubmit}>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="categoryid">
                                <Form.Label>Category ID:</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.categoryid}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                            </Form.Group>

                            <Form.Group as={Col} controlId="categoryname">
                                <Form.Label>Category Name:</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.categoryname}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="categorydisplayname">
                                <Form.Label>Display Name:</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={formData.categorydisplayname}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                            </Form.Group>

                            <Form.Group as={Col} controlId="isactive">
                                <Form.Label>Status:</Form.Label>
                                <div className="toggle-switch mt-2">
                                    <input
                                        type="checkbox"
                                        id="isactive"
                                        checked={formData.isactive === 1}
                                        onChange={handleChange}
                                        disabled={isSubmitting}
                                    />
                                    <span className="toggle-switch-label">Visible on Website</span>
                                </div>
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="categorydetails">
                                <Form.Label>Category Details:</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={formData.categorydetails}
                                    onChange={handleChange}
                                    disabled={isSubmitting}
                                />
                            </Form.Group>
                        </Row>

                        <div className="form-actions">
                            <button 
                                type="submit" 
                                className="btn-gradient-primary" 
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Update Category"}
                            </button>
                            <Link to="/categorylist" className="btn-back-link" style={{ marginLeft: '15px', textDecoration: 'none' }}>
                                ← Back to List
                            </Link>
                        </div>
                    </Form>
                </div>
            </div>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Update</Modal.Title>
                </Modal.Header>
                <Modal.Body>Save changes to this category?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleConfirmUpdate}>Confirm Update</Button>
                </Modal.Footer>
            </Modal>
            <ToastContainer />
        </div>
    );
};

export default CategoryUpdate;